import { get, getVersioned, post, seg } from '../client';
import type { ListParams, Page } from '../types/common';
import type { MessageThread, MessageThreadSummary, ReplyRequest, ThreadCommand, ThreadStatus } from '../types/messaging';

const mcmd = (id: string, c: string) => `/message-threads/${seg(id)}:${c}`;
const cmd = (id: string, c: ThreadCommand, body: object, etag: string | null) =>
  post<MessageThread>(mcmd(id, c), body, { ifMatch: etag, idempotent: true });

/** UI-only pool ids that are views, not backend pool keys (R-010): "mine" → assignee=me, "all" → no pool. */
const VIEW_POOLS = new Set(['mine', 'all', '']);

/**
 * Thread status groups for the Messages status filter. core-api accepts status=open|<thread status> (omit for all);
 * `threadInStatusView` mirrors it client-side so the mock and older servers give the same view.
 */
export type ThreadStatusView = 'open' | 'waiting_patient' | 'closed' | 'all';

export function threadInStatusView(status: ThreadStatus, view: ThreadStatusView | string): boolean {
  if (view === 'all') return true;
  if (view === 'closed') return status === 'closed';
  if (view === 'waiting_patient') return status === 'waiting_patient';
  return status !== 'closed' && status !== 'waiting_patient';
}

/**
 * Workforce thread rules (core-api care-coordination state machine), used when the backend gives no allowedActions:
 * reply only by the assigned responder from `assigned`; close only from `notified`; reopen from
 * closed/notified/sent/waiting_patient/delivery_failed; escalate from any non-closed, non-escalated state;
 * assign from routed/assigned/escalated/waiting_patient/notified/sent/delivery_failed.
 */
export function threadActions(t: Pick<MessageThread, 'status' | 'assignee' | 'allowedActions'>, meId: string): Set<ThreadCommand | (string & {})> {
  if (t.allowedActions) return new Set(t.allowedActions);
  const s = t.status;
  const out = new Set<ThreadCommand>();
  if (['routed', 'assigned', 'escalated', 'waiting_patient', 'notified', 'sent', 'delivery_failed'].includes(s)) out.add('assign');
  if (s === 'assigned' && t.assignee?.id === meId) out.add('reply');
  if (s === 'notified') out.add('close');
  if (['closed', 'notified', 'sent', 'waiting_patient', 'delivery_failed'].includes(s)) out.add('reopen');
  if (!['closed', 'escalated'].includes(s)) out.add('escalate');
  return out;
}

export const messagingApi = {
  /**
   * GET /message-threads: `pool` is a real pool key/name/id (never mine/all), `status` is open|<thread status>
   * (omitted for "all"), `assignee=me` for the "mine" view (R-010).
   */
  threads: (p: ListParams & { pool?: string; status?: string; patientId?: string; urgent?: boolean } = {}) =>
    get<Page<MessageThreadSummary>>('/message-threads', {
      limit: p.limit, cursor: p.cursor, patientId: p.patientId, urgent: p.urgent,
      pool: p.pool && !VIEW_POOLS.has(p.pool) ? p.pool : undefined,
      assignee: p.pool === 'mine' ? 'me' : undefined,
      status: !p.status || p.status === 'all' ? undefined : p.status,
    }),
  thread: (id: string) => getVersioned<MessageThread>(`/message-threads/${seg(id)}`),
  assign: (id: string, assigneeId: string, etag: string | null) => cmd(id, 'assign', { assigneeId }, etag),
  /**
   * Reply as the assigned responder; returns the updated thread (not a Message). AI provenance (aiInteractionId,
   * sectionId) is sent only via `aiProvenanceFor` — when the final text is exactly a section this user reviewed (R-030).
   */
  reply: (id: string, body: ReplyRequest, etag: string | null) =>
    cmd(id, 'reply', { body: body.body, ...(body.aiInteractionId ? { aiInteractionId: body.aiInteractionId } : {}), ...(body.sectionId ? { sectionId: body.sectionId } : {}) }, etag),
  close: (id: string, reason: string | undefined, etag: string | null) => cmd(id, 'close', reason ? { reason } : {}, etag),
  reopen: (id: string, etag: string | null, reason?: string) => cmd(id, 'reopen', reason ? { reason } : {}, etag),
  escalate: (id: string, reason: string, etag: string | null) => cmd(id, 'escalate', { reason }, etag),
};

/** An AI section the user reviewed and pulled into the reply. */
export interface UsedAiSection {
  interactionId: string;
  sectionId?: string;
  text: string;
}

/**
 * Provenance to send with a reply (R-030): core-api links the AI interaction only when the submitted text matches a
 * section this user reviewed. Anything else (edited after use, several sections combined) is sent as human-authored.
 */
export function aiProvenanceFor(finalText: string, used: UsedAiSection | null): Pick<ReplyRequest, 'aiInteractionId' | 'sectionId'> {
  if (!used || finalText.trim() !== used.text.trim()) return {};
  return { aiInteractionId: used.interactionId, ...(used.sectionId ? { sectionId: used.sectionId } : {}) };
}
